import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api } from "../api";
import { useApi } from "../hooks";
import Dialog from "./Dialog";
import { sentence } from "./Workflow";
import { confirmLeave, setLeaveGuard } from "../leave-guard";
export type Field = {
  key: string;
  label: string;
  type?: "text" | "textarea" | "number" | "date" | "email" | "url" | "select" | "color";
  options?: string[];
  /** How a select shows each option; the default is sentence case. */
  optionLabel?: (option: string) => string;
  required?: boolean;
  default?: string;
  min?: number;
  max?: number;
  /** Example value shown in the empty field. */
  placeholder?: string;
  /** One line under the field. */
  hint?: string;
  /** Two consecutive half-width fields share one row. */
  half?: boolean;
};

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** A colour field: a swatch picker and the hex text stay in step; the hex is what saves. */
function ColorField({ field, initial, disabled }: { field: Field; initial: string; disabled: boolean }) {
  const [hex, setHex] = useState(initial);
  const valid = HEX.test(hex);
  // No colour saved yet: the swatch shows an empty checkerboard, not the picker's
  // stand-in navy, so an empty field never looks like a chosen brand colour.
  return (
    <span className={valid ? "color-field" : "color-field is-empty"}>
      <input
        type="color"
        disabled={disabled}
        aria-label={`${field.label} swatch`}
        value={valid ? (hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex) : "#1d3043"}
        onChange={(e) => setHex(e.target.value)}
      />
      <input
        name={field.key}
        disabled={disabled}
        type="text"
        value={hex}
        onChange={(e) => setHex(e.target.value.trim())}
        placeholder={field.placeholder || "#1d3043"}
        pattern="^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$"
        title="A hex colour, e.g. #1d3043"
        spellCheck={false}
        required={field.required}
      />
    </span>
  );
}

export default function RecordForm({
  title,
  description,
  endpoint,
  method = "POST",
  fields,
  initial = {},
  submitLabel = "Save",
  protectChanges = false,
  onClose,
  onSaved,
}: {
  title: string;
  description?: string;
  endpoint: string;
  method?: string;
  fields: Field[];
  initial?: Record<string, unknown>;
  submitLabel?: string;
  /** Guard navigation and closing this form while edits have not been saved. */
  protectChanges?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const clients = useApi<Array<{ id: number; name: string }>>(fields.some(field => field.key === "client_id") ? "/api/clients" : null);
  const people = useApi<Array<{ id: number; full_name: string }>>(
    fields.some(field => field.key === "owner_id") ? "/api/tasks/assignees" : null,
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [formRevision, setFormRevision] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const baseline = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!formRef.current || busy) return;
    const current = JSON.stringify([...new FormData(formRef.current).entries()]);
    // Async client/owner choices may populate the untouched form after mount.
    if (baseline.current === null || formRevision === 0) baseline.current = current;
    setDirty(current !== baseline.current);
  }, [formRevision, clients.data, people.data]);
  const pending = useRef<symbol | null>(null);
  useEffect(() => () => { pending.current = null; }, []);
  useEffect(() => {
    if (!protectChanges) return;
    const atRisk = dirty || busy;
    setLeaveGuard(atRisk ? "You have unsaved changes or a save in progress. Leave this form?" : null);
    const warn = (event: BeforeUnloadEvent) => { if (atRisk) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => { setLeaveGuard(null); window.removeEventListener("beforeunload", warn); };
  }, [protectChanges, dirty, busy]);
  const close = () => {
    if (pending.current || (protectChanges && dirty && !confirmLeave())) return;
    onClose();
  };
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending.current) return;
    const request = Symbol();
    pending.current = request;
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const body: Record<string, unknown> = {};
    for (const field of fields) {
      const value = String(data.get(field.key) ?? "");
      body[field.key] =
        field.type === "number" ? (value === "" ? null : Number(value)) : value;
    }
    try {
      await api(endpoint, { method, body: JSON.stringify(body) });
      // A client/record change may have unmounted the form while it saved.
      if (pending.current !== request) return;
      if (protectChanges) setLeaveGuard(null);
      setDirty(false);
      onSaved();
      onClose();
    } catch (e) {
      if (pending.current === request) setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      if (pending.current === request) { pending.current = null; setBusy(false); }
    }
  }

  const control = (field: Field): ReactNode => {
    const value = String(initial[field.key] ?? field.default ?? "");
    if (["client_id", "owner_id"].includes(field.key)) {
      const list = field.key === "client_id"
        ? (clients.data || []).map((x) => ({ id: x.id, label: x.name }))
        : (people.data || []).map((x) => ({ id: x.id, label: x.full_name }));
      return (
        <select name={field.key} disabled={busy} required={field.required} defaultValue={String(initial[field.key] ?? "")}>
          <option value="">Choose {field.key === "client_id" ? "a client" : "an owner"}</option>
          {list.map((x) => <option value={x.id} key={x.id}>{x.label}</option>)}
        </select>
      );
    }
    if (field.type === "textarea")
      return <textarea name={field.key} disabled={busy} rows={3} defaultValue={value} required={field.required} placeholder={field.placeholder} />;
    if (field.type === "select")
      return (
        <select name={field.key} disabled={busy} defaultValue={String(initial[field.key] ?? field.default ?? field.options?.[0] ?? "")} required={field.required}>
          {field.options?.map((option) => <option key={option} value={option}>{(field.optionLabel || sentence)(option)}</option>)}
        </select>
      );
    if (field.type === "color") return <ColorField field={field} initial={value} disabled={busy} />;
    return (
      <input
        name={field.key}
        disabled={busy}
        type={field.type || "text"}
        defaultValue={value.slice(0, field.type === "date" ? 10 : undefined)}
        required={field.required}
        min={field.min}
        max={field.max}
        step={field.type === "number" ? "any" : undefined}
        placeholder={field.placeholder}
        inputMode={field.type === "number" ? "decimal" : undefined}
      />
    );
  };

  const labelled = (field: Field) => (
    <label key={field.key} className={field.type === "color" ? "field-color" : undefined}>
      <span className="field-label">
        {field.label}
        {field.required && <span className="field-required" aria-hidden="true"> *</span>}
      </span>
      {control(field)}
      {field.hint && <small className="field-hint">{field.hint}</small>}
    </label>
  );

  // Consecutive half-width fields pair up on one row; anything else takes the row.
  const rows: ReactNode[] = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    const next = fields[i + 1];
    if (f.half && next?.half) {
      rows.push(<div className="form-pair" key={`${f.key}+${next.key}`}>{labelled(f)}{labelled(next)}</div>);
      i++;
    } else rows.push(labelled(f));
  }

  return (
    <Dialog title={title} description={description} onClose={close}>
      <form ref={formRef} className="operation-form record-form" onSubmit={submit} onChange={() => setFormRevision(value => value + 1)}>
        {rows}
        {error && (
          <p className="inline-notice danger" role="alert">
            {error}
          </p>
        )}
        <div className="operation-actions">
          <button
            type="button"
            className="button secondary"
            onClick={close}
            disabled={busy}
          >
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            {busy ? "Saving…" : submitLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
